/**
 * 路径 → 画笔输入点（M4 补充：真实画笔描边路径，DESIGN.md §5.2/§6.3）。
 *
 * `flattenPath` 已经把路径拍成折线子路径；这类子路径在直线段上是**稀疏**
 * 的——`M 10 10 L 90 10` 只给两个端点，中间不插值（只有贝塞尔段才会按
 * `SEGMENTS_PER_CURVE` 加密）。这对直接画图（逐段连线）没问题，但喂给
 * `StrokeSampler` 就不行了：`StrokeSampler` 用 Catmull-Rom 在**控制点**
 * 之间插值，遇到折线的拐角（哪怕是简单的直角），样条会用相邻两段的方向
 * 算切线，把拐角"抹圆"、往拐角外侧弹出一截——控制点越稀疏，弹出幅度
 * 越大。真实 bug：拿一个矩形路径（4 个角点）描边，粗一点的笔刷在长边
 * 中段就会出现一整段没有笔迹的缺口（靠单测反复调整垫料点数量、打印
 * 逐像素覆盖度定位到的，缺口跟垫料多少无关，用直线两点单独喂样条完全
 * 连续，一旦加入相邻拐角就出现缺口，实锤是 Catmull-Rom 抹圆惹的祸）。
 *
 * 解决办法：先把折线按固定步长加密（`densify`）——不是重采样 stamp
 * 间距（那仍然是 `StrokeSampler` 自己按 spacing 决定的事），是保证喂给
 * 样条的**控制点**足够密，让每一段的切线始终贴着当前这条线段的方向，
 * 不会因为控制点太稀疏被邻近拐角"带偏"。
 *
 * 压感恒为 1（不是 null）：路径本身没有压感数据，`strokePathOnLayer`
 * 调用时会把 `pressureSize`/`pressureFlow` 都设成 false，`pressure` 的
 * 具体数值因此根本不参与 stamp 尺寸/流量计算，恒定为 1 只是避免落进
 * "无压感设备"的速度回退分支（那个分支没有意义——路径不是鼠标移动
 * 出来的，没有"速度"这个概念）。
 */
import type { InputPoint } from '../brush/sampler';
import type { Point } from './anchor';
import type { FlattenedSubpath } from './flatten';

/** 折线控制点加密的固定步长（文档像素）——足够密到让 Catmull-Rom 贴线，
 * 又不会为正常尺寸的路径生成过多点。跟画笔尺寸无关：即使笔刷很粗，
 * 拐角抹圆的幅度只取决于相邻控制点的绝对距离，不取决于笔刷粗细。 */
const DENSIFY_STEP = 6;

function densify(points: readonly Point[], step: number): Point[] {
  if (points.length < 2) return [...points];
  const out: Point[] = [points[0]!];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.max(1, Math.ceil(dist / step));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out;
}

export interface SubpathStrokeOptions {
  /**
   * 闭合路径首尾接缝的垫料点数（默认 3）。`StrokeSampler` 的 Catmull-Rom
   * 窗口假设"一笔画有明确起止点"：直接喂一条首点=尾点的闭合折线，
   * 起点处的曲率会当成"直线起笔"处理（复制端点凑段），跟真正连续绕一圈
   * 该有的曲率不一样，接缝处会有一道能看出来的"折角"。
   *
   * 解决办法：在真正的点序列前面借用路径尾部的几个点、后面借用路径头部
   * 的几个点，让 Catmull-Rom 在接缝两侧都拿到真实邻居而不是复制端点。
   * 借来的这几个点位置跟路径上已有的点重合，多出来的 stamp 落在早已被
   * 描过的地方，肉眼看不出"多一笔"，只是让接缝本身的曲率算对。
   */
  readonly seamPadding?: number;
}

const DEFAULT_SEAM_PADDING = 3;

/**
 * 一个折线子路径 → 一串 {@link InputPoint}。闭合子路径按 `seamPadding`
 * 补上首尾垫料点；开放子路径原样转换，不做任何接缝处理。
 */
export function subpathToInputPoints(
  subpath: FlattenedSubpath,
  options: SubpathStrokeOptions = {},
): InputPoint[] {
  if (subpath.points.length === 0) return [];
  const points = densify(subpath.points, DENSIFY_STEP);

  const toInput = (p: { readonly x: number; readonly y: number }, index: number): InputPoint => ({
    x: p.x,
    y: p.y,
    pressure: 1,
    timeMs: index,
  });

  if (!subpath.closed || points.length < 2) {
    return points.map((p, i) => toInput(p, i));
  }

  const pad = Math.max(0, Math.min(options.seamPadding ?? DEFAULT_SEAM_PADDING, points.length - 1));
  const tailPad = points.slice(points.length - pad);
  // 从 p1 开始（不是 p0）：闭合点 p0 已经作为"回到起点"的那一步单独喂过，
  // 这里再借的是"绕过起点之后"的下一批点，不能把 p0 自己重复两次
  const headPad = points.slice(1, 1 + pad);
  const sequence = [...tailPad, ...points, points[0]!, ...headPad];
  return sequence.map((p, i) => toInput(p, i));
}
